import { useState, useRef, useEffect, useCallback, cloneElement } from 'react';
import { Box, Popover, Typography, ClickAwayListener } from '@mui/material';
import { useTheme } from '@material-ui/core';

// Componente que renderiza um popup on hover no componente filho

// Conteúdo do popup pode ser um texto simples ou componentes personalizados
// Popup não some se o mouse permanecer em cima dele ou do child
// É possível interagir com os componentes dentro do popup
// Utilizando requestAnimationFrame no lugar de setTimeout para corrigir inconsistências e problemas de race conditions
// Detecção onHover e onClick para corrigir problemas do popup permanecer na tela ao clicar em componentes como selects/dropdowns
// O estilo/layout do child não é afetado

export default function InfoPopover({
  children, // Componente filho ( que recebe o popup )
  content, // Conteúdo do popup ( texto ou componentes ) ( Não mostra o popup se for null ou '' )
  popoverProps = {}, // Substitui/adiciona propriedades ao popup 
  showDelay = 300, // Delay em ms para aparecer o popup
  hideDelay = 100, // Delay em ms para sumir o popup
  popoverPadding = 2, // Padding do popover
  popoverBackgroundColor = null, // Cor do background do popover
  popoverColor = null, // Cor do texto do popover
  popoverMaxWidth = 350 // Cor do texto do popover
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const rafRef = useRef(null);
  const hoverState = useRef({ overChild: false, overPopover: false });
  const interactingRef = useRef(false);
  const targetRef = useRef(null);
  const theme = useTheme();
  popoverBackgroundColor = popoverBackgroundColor ?? theme.palette.background.default
  popoverColor = popoverColor ?? theme.palette.text.primary

  const hasContent =
    content !== null &&
    content !== undefined &&
    !(typeof content === 'string' && content.trim() === '');

  const open = Boolean(anchorEl) && hasContent;

  const cancelRaf = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const requestOpen = useCallback(
    (target) => {
      cancelRaf();
      targetRef.current = target;
      const start = performance.now();
      const tick = (now) => {
        if (now - start >= showDelay) {
          if (hoverState.current.overChild || hoverState.current.overPopover) {
            setAnchorEl(targetRef.current);
          }
        } else {
          rafRef.current = requestAnimationFrame(tick);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    [showDelay]
  );

  const requestClose = useCallback(() => {
    cancelRaf();
    const start = performance.now();
    const tick = (now) => {
      if (now - start >= hideDelay) {
        if (
          !hoverState.current.overChild &&
          !hoverState.current.overPopover &&
          !interactingRef.current
        ) {
          setAnchorEl(null);
        }
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [hideDelay]);

  const handleChildMouseEnter = (e) => {
    if (!hasContent) return;
    hoverState.current.overChild = true;
    requestOpen(e.currentTarget);
    if (children.props.onMouseEnter) children.props.onMouseEnter(e);
  };

  const handleChildMouseLeave = (e) => {
    hoverState.current.overChild = false;
    requestClose();
    if (children.props.onMouseLeave) children.props.onMouseLeave(e);
  };

  const handleChildMouseDown = (e) => {
    interactingRef.current = true;
    cancelRaf();
    if (children.props.onMouseDown) children.props.onMouseDown(e);
  };

  const handleChildMouseUp = (e) => {
    interactingRef.current = false;
    requestClose();
    if (children.props.onMouseUp) children.props.onMouseUp(e);
  };

  const handlePopoverMouseEnter = () => {
    hoverState.current.overPopover = true;
    cancelRaf();
  };

  const handlePopoverMouseLeave = () => {
    hoverState.current.overPopover = false;
    requestClose();
  };

  useEffect(() => {
    return () => cancelRaf();
  }, []);

  useEffect(() => {
    if (!anchorEl) return;
    const observer = new MutationObserver(() => {
      if (!document.body.contains(anchorEl)) {
        setAnchorEl(null);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [anchorEl]);

  if (!hasContent) return children;

  const childWithHandlers = cloneElement(children, {
    onMouseEnter: handleChildMouseEnter,
    onMouseLeave: handleChildMouseLeave,
    onMouseDown: handleChildMouseDown,
    onMouseUp: handleChildMouseUp,
  });

  return (
    <div>
      {childWithHandlers}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: popoverBackgroundColor,
              color: popoverColor,
              boxShadow: theme.shadows[6],
            }
          }
        }}
        {...popoverProps}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Box
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
            sx={{
              p: popoverPadding,
              maxWidth: popoverMaxWidth,
              pointerEvents: 'auto',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
              <Typography>{content}</Typography>
          </Box>
        </ClickAwayListener>
      </Popover>
    </div>
  );
}
